import { X, Send, Pencil, Paperclip, Loader2, Mail } from "lucide-react";
import { LETTER_CSS, sanitizeEmailHtml, applyJustifyTypography } from "../lib/letterPdf";

const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const Row = ({ label, people }) =>
  people.length ? (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-500 w-16 shrink-0">{label}</span>
      <span className="text-gray-800 break-all">{people.map((p) => (p.name ? `${p.name} <${p.email}>` : p.email)).join(", ")}</span>
    </div>
  ) : null;

export default function EmailPreviewModal({ open, onClose, onSend, sending, from, to, cc, ccn, subject, body, bodyIsHtml, letterHtml, signature, attachments, phaseLabel, progressPct, headerHtml }) {
  if (!open) return null;

  const content = (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/70 p-4" data-testid="email-preview-modal">
      <div className="bg-white text-gray-900 rounded-lg shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="font-semibold text-gray-800 flex items-center gap-2"><Mail className="h-4 w-4 text-amber-600" /> Anteprima email</div>
          <button type="button" onClick={onClose} data-testid="email-preview-close" className="text-gray-400 hover:text-gray-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-3 border-b border-gray-200 space-y-1">
            {from && (
              <div className="flex gap-2 text-sm">
                <span className="text-gray-500 w-16 shrink-0">Da</span>
                <span className="text-gray-800 break-all">{from}</span>
              </div>
            )}
            <Row label="A" people={to} />
            <Row label="CC" people={cc} />
            <Row label="CCN" people={ccn} />
            <div className="flex gap-2 text-base font-semibold pt-1">
              <span className="text-gray-500 w-16 shrink-0 text-sm font-normal pt-0.5">Oggetto</span>
              <span className="text-gray-900" data-testid="email-preview-subject">{subject || "(nessun oggetto)"}</span>
            </div>
          </div>

          <div className="px-5 py-4 space-y-3" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
            {headerHtml ? <div data-testid="email-preview-header" dangerouslySetInnerHTML={{ __html: headerHtml }} /> : null}
            {body ? (
              bodyIsHtml ? (
                <div className="pgp-pdfroot text-[15px] leading-relaxed text-gray-800 overflow-x-hidden" data-testid="email-preview-body">
                  <style>{LETTER_CSS}</style>
                  <div dangerouslySetInnerHTML={{ __html: body }} />
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-800" data-testid="email-preview-body">{body}</div>
              )
            ) : null}
            {letterHtml ? (
              <div className="pgp-pdfroot text-gray-900 overflow-x-hidden" data-testid="email-preview-letter">
                <style>{LETTER_CSS}</style>
                <div className="pgp-doc" dangerouslySetInnerHTML={{ __html: applyJustifyTypography(sanitizeEmailHtml(letterHtml)) }} />
              </div>
            ) : null}
            {signature ? (
              <div className="whitespace-pre-wrap text-[15px] leading-relaxed text-gray-800">{`--\n${signature}`}</div>
            ) : null}
            {!body && !letterHtml && !signature ? (
              <span className="text-gray-400" data-testid="email-preview-body">(messaggio vuoto)</span>
            ) : null}
          </div>

          {attachments.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-200">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{attachments.length} allegat{attachments.length === 1 ? "o" : "i"}</div>
              <div className="flex flex-wrap gap-2" data-testid="email-preview-attachments">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700">
                    <Paperclip className="h-3.5 w-3.5 text-amber-600" />
                    <span className="truncate max-w-[220px]">{a.nome}</span>
                    {a.size != null && <span className="text-xs text-gray-400 font-mono">{fmtBytes(a.size)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {sending && phaseLabel && (
          <div className="px-5 py-2.5 border-t border-gray-200 bg-amber-50" data-testid="email-preview-progress">
            <div className="flex items-center justify-between text-xs text-amber-700 mb-1.5">
              <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {phaseLabel}</span>
              <span className="font-mono" data-testid="email-preview-progress-pct">{progressPct}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-amber-100 overflow-hidden">
              <div className="h-full rounded-full bg-amber-500 transition-all duration-300" style={{ width: `${progressPct}%` }} data-testid="email-preview-progress-bar" />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button type="button" onClick={onClose} data-testid="email-preview-edit"
            className="flex items-center gap-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 px-4 py-2">
            <Pencil className="h-4 w-4" /> Modifica
          </button>
          <button type="button" onClick={onSend} disabled={sending} data-testid="email-preview-send"
            className="flex items-center gap-1.5 text-sm rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-[#0a0d14] font-semibold px-4 py-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia ora
          </button>
        </div>
      </div>
    </div>
  );

  return content;
}
